<template>
  <div class="create-campaign-review-card create-campaign-review-card-email-settings">
    <div class="create-campaign-review-card__text-wrapper">
      <h2>Email settings</h2>

      <a href="#" @click.prevent="$emit('edit')">Edit</a>
    </div>

    <hr>

    <div class="create-campaign-review-card-email-settings__row">
      <span>Email subject line</span>
      <strong>{{ emailSubjectLine }}</strong>
    </div>

    <div class="create-campaign-review-card-email-settings__row">
      <div class="create-campaign-review-card-email-settings__label-with-tooltip">
        <span>Email body text</span>

        <common-tooltip
          right
          :top="false"
          :max-width="null"
          :z-index="8"
          :activator-size="15"
          activator-color="#62646A"
          class="create-campaign-review-card-email-settings__tooltip-icon"
        >
          <img alt="" width="500" height="303" :src="emailBodyTooltipImage" />
        </common-tooltip>
      </div>

      <strong v-if="emailBodyText" v-html="sanitizedEmailBodyText" />
      <strong v-else>—</strong>
    </div>

    <div class="create-campaign-review-card-email-settings__row">
      <span>Gift giver email</span>
      <strong>{{ giftGiverEmail || '—' }}</strong>
    </div>

    <div class="create-campaign-review-card-email-settings__row">
      <div class="create-campaign-review-card-email-settings__label-with-tooltip">
        <span>Main button label</span>

        <common-tooltip
          right
          :top="false"
          :max-width="null"
          :z-index="8"
          :activator-size="15"
          activator-color="#62646A"
          class="create-campaign-review-card-email-settings__tooltip-icon"
        >
          <img alt="" width="500" height="265" :src="emailButtonTooltipImage" />
        </common-tooltip>
      </div>

      <strong>{{ mainButtonLabel || '—' }}</strong>
    </div>

    <hr>

    <div class="create-campaign-review-card-email-settings__footer">
      <span v-if="customSettingsCount">
        {{ customSettingsCount }} custom {{ customSettingsCount === 1 ? 'setting' : 'settings' }} beyond the defaults
      </span>

      <cg-button @click.prevent="$emit('edit')">Manage all settings</cg-button>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import DOMPurify from 'dompurify'
import { CgButton } from '@corporategiftcom/design-system'
import { formDefaultState } from '@/store/newCampaign/formDefaultState'

const { mapState } = createNamespacedHelpers('newCampaign')
const defaultAdvancedOptions = formDefaultState().advancedOptions
const EMAIL_BODY_ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'br']

export default {
  name: 'CreateCampaignReviewCardEmailSettings',
  components: { CgButton },
  computed: {
    ...mapState(['form']),
    emailSubjectLine () { return this.form.advancedOptions.emailSubjectLine },
    emailBodyText () { return this.form.advancedOptions.emailBodyText },
    sanitizedEmailBodyText () {
      return DOMPurify.sanitize(this.emailBodyText, { ALLOWED_TAGS: EMAIL_BODY_ALLOWED_TAGS, ALLOWED_ATTR: [] })
    },
    giftGiverEmail () { return this.form.advancedOptions.giftGiverEmail },
    mainButtonLabel () { return this.form.advancedOptions.mainButtonLabel },
    emailBodyTooltipImage () { return require('@/assets/createCampaign/email-body.png') },
    emailButtonTooltipImage () { return require('@/assets/createCampaign/email-button.png') },
    customSettingsCount () {
      const { advancedOptions } = this.form
      // Fields already shown directly in this card aren't counted as "custom settings beyond the defaults".
      const shownDirectly = ['emailSubjectLine', 'emailBodyText', 'giftGiverEmail', 'mainButtonLabel', 'successButton']

      const changedFields = Object.keys(defaultAdvancedOptions)
        .filter((key) => !shownDirectly.includes(key))
        .filter((key) => advancedOptions[key] !== defaultAdvancedOptions[key])
        .length

      const successButtonChanged = advancedOptions.successButton.addButton !== defaultAdvancedOptions.successButton.addButton

      return changedFields + (successButtonChanged ? 1 : 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.create-campaign-review-card-email-settings {
  background: rgb(246, 245, 255);

  &__row {
    display: flex;
    flex-direction: column;
    width: 100%;

    & > span {
      font-family: 'Lato-Italic', sans-serif;
      font-size: 14px;
      color: #62646A;
    }

    & > strong {
      font-family: 'Lato-Regular', sans-serif;
      font-weight: 400;
      font-size: 16px;
      color: #222325;
      word-break: break-word;
    }
  }

  &__label-with-tooltip {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: nowrap;
    line-height: 20px;
    gap: 6px;

    & > span {
      font-family: 'Lato-Italic', sans-serif;
      font-size: 14px;
      color: #62646A;
    }
  }

  &__tooltip-icon {
    margin-bottom: 2px;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    align-items: flex-start;

    & > span {
      font-family: 'Lato-Italic', sans-serif;
      font-size: 14px;
      color: #95979D;
    }

    & > button {
      height: 40px;
      padding: 0 20px;
      border-radius: 100px;
      border: 2px solid rgb(0, 0, 0);
      background: rgb(255, 255, 255);
      color: rgb(0, 0, 0);
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      align-self: flex-start;

      &:hover {
        background: rgb(255, 255, 255) !important;
        border-color: rgb(98, 100, 106) !important;
        color: rgb(98, 100, 106) !important;
      }
    }
  }
}
</style>
